import { useCallback, useMemo } from "react";

import { useIsNodeOrImpersonated } from "@js/apps/common/hooks/use-is-node-or-impersonated";
import { Snackbar } from "@js/components/snackbar";
import { useAppSelector } from "@js/hooks/redux";
import type { EmployerBidListBid, Job } from "@js/types/jobs";
import { pluralize } from "@js/utils";

import { useBulkSendApplicationDataMutation } from "../../api";
import { getSelectedBidIds } from "../../components/bulk-select-actions/bid-bulk-selection-slice";

export const useBulkSendApplicationData = ({
  job,
  bidList,
}: {
  job: Pick<Job, "integration_label" | "is_open">;
  bidList: EmployerBidListBid[];
}) => {
  const selectedBidIds = useAppSelector(getSelectedBidIds);
  const selectedBids = useMemo(
    () => bidList.filter((bid) => selectedBidIds.includes(bid.id)),
    [bidList, selectedBidIds],
  );

  const isNodeOrImpersonated = useIsNodeOrImpersonated();

  const [bulkSendApplicationData, { isLoading }] =
    useBulkSendApplicationDataMutation();

  const canSendApplicationData =
    isNodeOrImpersonated && !!job.integration_label && !!job.is_open;

  const selectedDataToSend = useMemo(() => {
    return selectedBids.filter(
      (bid) => !bid.external_status && !bid.freelancer.approved,
    );
  }, [selectedBids]);

  const handleSendSelectedApplications = useCallback(async () => {
    if (!canSendApplicationData || isLoading) {
      return;
    }
    const selectedApplicationIdsToSend = selectedDataToSend.map(
      (bid) => bid.id,
    );

    try {
      await bulkSendApplicationData({
        pks: selectedApplicationIdsToSend,
      }).unwrap();

      Snackbar.success(
        `Application${pluralize(selectedApplicationIdsToSend.length)} data sent. It might take a few minutes to sync.`,
      );
    } catch (_error) {
      Snackbar.error(
        `Sending application${pluralize(selectedApplicationIdsToSend.length)} failed. Please refresh the page and try again`,
      );
    }
  }, [
    bulkSendApplicationData,
    canSendApplicationData,
    selectedDataToSend,
    isLoading,
  ]);

  const isSendingApplicationsBlocked = !selectedDataToSend.length;

  return {
    handleSendSelectedApplications,
    isLoading,
    canSendApplicationData,
    isSendingApplicationsBlocked,
  };
};
